/**
 * RemediationScriptButton Component (DC-1434)
 *
 * Prominent CTA button that fetches structured remediation sections
 * from the API and opens the RemediationScriptViewer modal.
 *
 * AI availability is checked at click time (not pre-gated) so the
 * button is always visible when results exist.
 */

import React, { useState, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { RemediationScriptViewer } from './RemediationScriptViewer';
import type { RemediationScriptViewerProps } from './RemediationScriptViewer';

type StructuredRemediationResult = RemediationScriptViewerProps['data'];

export interface DatabricksSourceInfo {
  connectionId: string;
  connectionName: string;
  catalog: string;
  schema: string;
  table: string;
  workspaceUrl: string;
}

interface RemediationScriptButtonProps {
  journeyId: string;
  disabled?: boolean;
  databricksSource?: DatabricksSourceInfo;
}

export const RemediationScriptButton: React.FC<RemediationScriptButtonProps> = ({
  journeyId,
  disabled = false,
  databricksSource,
}) => {
  const { authFetch } = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewerData, setViewerData] = useState<StructuredRemediationResult | null>(null);
  const [showViewer, setShowViewer] = useState(false);

  const fetchRemediationSections = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(
        `/api/contextual-validity/journeys/${journeyId}/remediation-sections`
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to generate script' }));
        const msg = errorData.error?.message || errorData.error || errorData.message || 'Failed to generate remediation script';

        if (response.status === 503) {
          throw new Error('AI generation is not available. Please configure your Anthropic API key.');
        }
        throw new Error(msg);
      }

      const data: StructuredRemediationResult = await response.json();
      setViewerData(data);
      setShowViewer(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [journeyId, authFetch]);

  const handleClick = useCallback(async () => {
    if (loading || disabled) return;
    await fetchRemediationSections();
  }, [loading, disabled, fetchRemediationSections]);

  const handleRegenerate = useCallback(async () => {
    if (loading) return;
    await fetchRemediationSections();
  }, [loading, fetchRemediationSections]);

  const handleCloseViewer = useCallback(() => {
    setShowViewer(false);
  }, []);

  return (
    <>
      <button
        type="button"
        className="btn btn-primary remediation-cta-btn"
        onClick={handleClick}
        disabled={disabled || loading}
        title="Generate a Python remediation script from analysis findings"
      >
        {loading ? (
          <>
            <span className="spinner-sm" aria-hidden="true" />
            {' '}Generating Script...
          </>
        ) : (
          <>
            &#9889; Review &amp; Export Script
          </>
        )}
      </button>

      {error && (
        <div className="remediation-cta-error">
          <span className="error-icon">&#9888;</span>
          {error}
          <button
            type="button"
            className="btn-link"
            onClick={handleClick}
          >
            Retry
          </button>
          <button
            type="button"
            className="btn-link"
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {showViewer && viewerData && (
        <RemediationScriptViewer
          isOpen={showViewer}
          onClose={handleCloseViewer}
          data={viewerData}
          journeyId={journeyId}
          onRegenerate={handleRegenerate}
          regenerating={loading}
          databricksSource={databricksSource}
        />
      )}
    </>
  );
};

export default RemediationScriptButton;
